import { z } from 'zod';

export const RawEventSourceSchema = z.enum([
  'middleware.beforeModel',
  'middleware.afterModel',
  'middleware.wrapModelCall',
  'middleware.wrapToolCall',
  'provider.stream',
  'tool.result',
  'todo.middleware',
  'subagent',
]);
export type RawEventSource = z.infer<typeof RawEventSourceSchema>;

export const RawAgentEventSchema = z.object({
  id: z.string(),
  queryIdentifier: z.string(),
  sequence: z.number(),
  timestamp: z.number(),
  source: RawEventSourceSchema,
  name: z.string(),
  payload: z.unknown(),
});
export type RawAgentEvent = z.infer<typeof RawAgentEventSchema>;

export const AgentEventTypeSchema = z.enum([
  'scope_started',
  'scope_completed',
  'scope_failed',
  'thinking_started',
  'thinking_updated',
  'thinking_completed',
  'thought_stream_started',
  'thought_stream_delta',
  'thought_stream_completed',
  'tool_call_started',
  'tool_call_completed',
  'tool_call_failed',
  'message_started',
  'message_delta',
  'message_completed',
  'todos_replaced',
  'query_completed',
  'query_failed',
]);
export type AgentEventType = z.infer<typeof AgentEventTypeSchema>;

export const AgentEventSchema = z.object({
  id: z.string(),
  queryIdentifier: z.string(),
  sequence: z.number(),
  timestamp: z.number(),
  type: AgentEventTypeSchema,
  scopeId: z.string(),
  parentBlockId: z.string().optional(),
  payload: z.record(z.string(), z.unknown()),
});
export type AgentEvent = z.infer<typeof AgentEventSchema>;

export const TodoStatusSchema = z.enum(['pending', 'in_progress', 'completed']);
export type TodoStatus = z.infer<typeof TodoStatusSchema>;

export const MiddlewareTodoSchema = z.object({
  content: z.string(),
  status: TodoStatusSchema,
});
export type MiddlewareTodo = z.infer<typeof MiddlewareTodoSchema>;

export const QueryStatusSchema = z.enum([
  'idle',
  'thinking',
  'running_tool',
  'streaming',
  'done',
  'error',
]);
export type QueryStatus = z.infer<typeof QueryStatusSchema>;

export const BlockStatusSchema = z.enum(['pending', 'running', 'completed', 'error']);
export type BlockStatus = z.infer<typeof BlockStatusSchema>;

export const ScopeStatusSchema = z.enum(['running', 'completed', 'error']);
export type ScopeStatus = z.infer<typeof ScopeStatusSchema>;

export const ScopeKindSchema = z.enum(['agent', 'subagent', 'tool', 'task']);
export type ScopeKind = z.infer<typeof ScopeKindSchema>;

export const BlockKindSchema = z.enum([
  'scope',
  'thinking',
  'thought_stream',
  'tool_call',
  'message',
  'error',
]);
export type BlockKind = z.infer<typeof BlockKindSchema>;

export const TodoItemSchema = z.object({
  key: z.string(),
  content: z.string(),
  status: TodoStatusSchema,
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type TodoItem = z.infer<typeof TodoItemSchema>;

const BaseBlockSchema = z.object({
  id: z.string(),
  kind: BlockKindSchema,
  createdAt: z.number(),
  updatedAt: z.number(),
  status: BlockStatusSchema,
  scopeId: z.string(),
  parentBlockId: z.string().optional(),
  childBlockIds: z.array(z.string()),
  depth: z.number(),
});
export type BaseBlock = z.infer<typeof BaseBlockSchema>;

export const ScopeBlockSchema = BaseBlockSchema.extend({
  kind: z.literal('scope'),
  scopeKind: ScopeKindSchema,
  name: z.string(),
});
export type ScopeBlock = z.infer<typeof ScopeBlockSchema>;

export const ThinkingBlockSchema = BaseBlockSchema.extend({
  kind: z.literal('thinking'),
  summary: z.string().optional(),
});
export type ThinkingBlock = z.infer<typeof ThinkingBlockSchema>;

export const ThoughtStreamBlockSchema = BaseBlockSchema.extend({
  kind: z.literal('thought_stream'),
  provider: z.string().optional(),
  text: z.string(),
  streaming: z.boolean(),
  isHidden: z.boolean().optional(),
});
export type ThoughtStreamBlock = z.infer<typeof ThoughtStreamBlockSchema>;

export const ToolCallBlockSchema = BaseBlockSchema.extend({
  kind: z.literal('tool_call'),
  toolCallId: z.string(),
  name: z.string(),
  input: z.unknown().optional(),
  output: z.unknown().optional(),
  startedAt: z.number().optional(),
  completedAt: z.number().optional(),
});
export type ToolCallBlock = z.infer<typeof ToolCallBlockSchema>;

export const MessageBlockSchema = BaseBlockSchema.extend({
  kind: z.literal('message'),
  role: z.literal('assistant'),
  text: z.string(),
  streaming: z.boolean(),
});
export type MessageBlock = z.infer<typeof MessageBlockSchema>;

export const ErrorBlockSchema = BaseBlockSchema.extend({
  kind: z.literal('error'),
  message: z.string(),
  details: z.unknown().optional(),
});
export type ErrorBlock = z.infer<typeof ErrorBlockSchema>;

export const UIBlockSchema = z.discriminatedUnion('kind', [
  ScopeBlockSchema,
  ThinkingBlockSchema,
  ThoughtStreamBlockSchema,
  ToolCallBlockSchema,
  MessageBlockSchema,
  ErrorBlockSchema,
]);
export type UIBlock = z.infer<typeof UIBlockSchema>;

export const ExecutionScopeSchema = z.object({
  id: z.string(),
  kind: ScopeKindSchema,
  name: z.string(),
  parentScopeId: z.string().optional(),
  parentBlockId: z.string().optional(),
  startedAt: z.number(),
  completedAt: z.number().optional(),
  status: ScopeStatusSchema,
});
export type ExecutionScope = z.infer<typeof ExecutionScopeSchema>;

export const AgentProgressSchema = z.object({
  status: QueryStatusSchema,
  blocks: z.record(z.string(), UIBlockSchema),
  rootTimeline: z.array(z.string()),
  scopes: z.record(z.string(), ExecutionScopeSchema),
  rootScopeId: z.string(),
  todos: z.array(TodoItemSchema),
  activeBlockId: z.string().optional(),
  lastSequenceApplied: z.number(),
});
export type AgentProgress = z.infer<typeof AgentProgressSchema>;